/**
 * Stock Item Delete Validation
 * 
 * This module provides validation logic for deleting stock items.
 * It ensures data integrity by preventing deletion of items that are:
 * - In use (have quantities)
 * - Referenced in transactions
 * - Part of active business processes
 */

import { StockItem } from "@/types/stock-item";

export interface DeleteValidationResult {
  canDelete: boolean;
  blockingReasons: string[];
  warnings: string[];
  affectedRecords?: {
    stockMovements?: number;
    salesOrders?: number;
    purchaseOrders?: number;
    stockTakes?: number;
  };
}

export interface DeleteValidationOptions {
  checkStockMovements?: boolean;
  checkOrders?: boolean;
  checkStockTakes?: boolean;
  includeWarnings?: boolean;
}

/**
 * Validates whether a stock item can be deleted
 * @param item The stock item to validate
 * @param options Validation options
 * @returns Validation result with blocking reasons and warnings
 */
export function validateStockItemDeletion(
  item: StockItem,
  options: DeleteValidationOptions = {}
): DeleteValidationResult {
  const {
    checkStockMovements = true,
    checkOrders = true,
    checkStockTakes = true,
    includeWarnings = true,
  } = options;

  const blockingReasons: string[] = [];
  const warnings: string[] = [];
  const affectedRecords: DeleteValidationResult["affectedRecords"] = {};

  // PRIMARY VALIDATION RULES (Must Block Delete)

  // Rule 1: Check Physical Quantity
  if (item.physicalQuantity > 0) {
    blockingReasons.push(
      `Item has physical quantity of ${item.physicalQuantity} ${item.unitOfMeasure}. Stock must be zero before deletion.`
    );
  }

  // Rule 2: Check Allocated Quantity
  if (item.allocatedQuantity > 0) {
    blockingReasons.push(
      `Item has ${item.allocatedQuantity} ${item.unitOfMeasure} allocated to orders. Remove allocations before deletion.`
    );
  }

  // Rule 3: Check Quantity Due In
  if (item.quantityDueIn > 0) {
    blockingReasons.push(
      `Item has ${item.quantityDueIn} ${item.unitOfMeasure} on order from suppliers. Complete or cancel purchase orders before deletion.`
    );
  }

  // Rule 4: Check Stock Movements (would need API call in real implementation)
  // For now, we'll check if there's any historical activity
  if (checkStockMovements && (item.totalCostOfSales > 0 || item.totalValueOfSales > 0)) {
    // This indicates the item has been used in transactions
    blockingReasons.push(
      `Item has transaction history (Total Sales: $${item.totalValueOfSales.toFixed(2)}). Items with transaction history cannot be deleted for audit compliance.`
    );
  }

  // Rule 5 & 6: Check Orders (would need API calls in real implementation)
  // These would be implemented when Sales Orders and Purchase Orders are added
  // if (checkOrders) {
  //   const ordersCheck = await checkItemInOrders(item.code);
  //   if (ordersCheck.inSalesOrders) {
  //     blockingReasons.push(`Item is referenced in ${ordersCheck.salesOrderCount} sales order(s).`);
  //     affectedRecords.salesOrders = ordersCheck.salesOrderCount;
  //   }
  //   if (ordersCheck.inPurchaseOrders) {
  //     blockingReasons.push(`Item is referenced in ${ordersCheck.purchaseOrderCount} purchase order(s).`);
  //     affectedRecords.purchaseOrders = ordersCheck.purchaseOrderCount;
  //   }
  // }

  // Rule 7: Check Active Stock Takes (would need API call in real implementation)
  // if (checkStockTakes) {
  //   const stockTakeCheck = await checkItemInActiveStockTakes(item.code);
  //   if (stockTakeCheck.inActiveStockTake) {
  //     blockingReasons.push(`Item is part of ${stockTakeCheck.stockTakeCount} active stock take(s).`);
  //     affectedRecords.stockTakes = stockTakeCheck.stockTakeCount;
  //   }
  // }

  // SECONDARY VALIDATION RULES (Warnings Only)
  if (includeWarnings) {
    // Rule 8: Historical Data Warning
    if (item.totalCostOfSales > 0 || item.totalValueOfSales > 0) {
      warnings.push(
        `Item has historical sales data. Consider marking as inactive instead of deleting.`
      );
    }

    // Rule 9: Recently Active Warning
    if (item.lastMovementDate) {
      const daysSinceLastMovement = Math.floor(
        (Date.now() - new Date(item.lastMovementDate).getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastMovement <= 30) {
        warnings.push(
          `Item was last moved ${daysSinceLastMovement} day(s) ago. Ensure this deletion is intentional.`
        );
      }
    }

    // Rule 10: Supplier Information Warning
    if (item.supplier) {
      warnings.push(
        `Item has supplier information configured (${item.supplier}). This configuration will be lost.`
      );
    }

    // Additional warning for items with reorder points
    if (item.reorderPoint > 0 || item.reorderQuantity > 0) {
      warnings.push(
        `Item has reorder settings configured. Automatic reordering will be disabled.`
      );
    }
  }

  return {
    canDelete: blockingReasons.length === 0,
    blockingReasons,
    warnings,
    affectedRecords: Object.keys(affectedRecords).length > 0 ? affectedRecords : undefined,
  };
}

/**
 * Validates deletion of multiple stock items
 * @param items Array of stock items to validate
 * @param options Validation options
 * @returns Map of item codes to their validation results
 */
export function validateBulkStockItemDeletion(
  items: StockItem[],
  options: DeleteValidationOptions = {}
): Map<string, DeleteValidationResult> {
  const results = new Map<string, DeleteValidationResult>();

  for (const item of items) {
    const result = validateStockItemDeletion(item, options);
    results.set(item.code, result);
  }

  return results;
}

/**
 * Gets a summary of bulk deletion validation
 * @param validationResults Map of validation results
 * @returns Summary statistics
 */
export function getBulkDeleteValidationSummary(
  validationResults: Map<string, DeleteValidationResult>
) {
  let canDeleteCount = 0;
  let blockedCount = 0;
  let warningCount = 0;
  const allBlockingReasons = new Set<string>();
  const allWarnings = new Set<string>();

  for (const result of validationResults.values()) {
    if (result.canDelete) {
      canDeleteCount++;
    } else {
      blockedCount++;
    }

    if (result.warnings.length > 0) {
      warningCount++;
    }

    result.blockingReasons.forEach((reason) => {
      // Extract the general reason (before specific details)
      const generalReason = reason.split(".")[0];
      allBlockingReasons.add(generalReason);
    });

    result.warnings.forEach((warning) => {
      const generalWarning = warning.split(".")[0];
      allWarnings.add(generalWarning);
    });
  }

  return {
    totalItems: validationResults.size,
    canDeleteCount,
    blockedCount,
    warningCount,
    hasBlockedItems: blockedCount > 0,
    hasWarnings: warningCount > 0,
    commonBlockingReasons: Array.from(allBlockingReasons),
    commonWarnings: Array.from(allWarnings),
  };
}

/**
 * Formats validation results for display in UI
 * @param result Validation result
 * @returns Formatted message
 */
export function formatValidationMessage(result: DeleteValidationResult): string {
  if (result.canDelete && result.warnings.length === 0) {
    return "This item can be safely deleted.";
  }

  const messages: string[] = [];

  if (result.blockingReasons.length > 0) {
    messages.push("Cannot delete this item:");
    messages.push(...result.blockingReasons.map((r) => `• ${r}`));
  }

  if (result.warnings.length > 0) {
    if (messages.length > 0) messages.push("");
    messages.push("Warnings:");
    messages.push(...result.warnings.map((w) => `⚠ ${w}`));
  }

  return messages.join("\n");
}
